// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — Shift Staffing Analyzer
// Real labor-cost-to-revenue correlation per shift per branch.
// Unlike the old evening-hours rule, this uses ACTUAL wages from
// shift_staffing table vs ACTUAL revenue from sales_daily.
// Flags shifts where labor cost exceeds threshold of revenue.
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Analyze all active shifts and compute labor efficiency.
 * @param {Array} shiftStaffing - rows from shift_staffing table
 * @param {Array} sales - rows from sales_daily
 * @param {Array} branches
 * @returns {Array<ShiftAnalysis>}
 *   - branch_id, branch_name_ar/en, shift, days_analyzed
 *   - avg_daily_revenue, avg_daily_labor_cost
 *   - labor_to_revenue_ratio, revenue_per_labor_sar
 *   - status: 'healthy' | 'watch' | 'unprofitable'
 *   - estimated_monthly_waste_sar (if unprofitable)
 */
export function analyzeShifts({ shiftStaffing = [], sales = [], branches = [] }) {
  if (!shiftStaffing.length || !sales.length) return [];

  const branchMap = new Map(branches.map(b => [b.id, b]));
  const results = [];

  for (const shift of shiftStaffing.filter(s => s.is_active !== false)) {
    const branch = branchMap.get(shift.branch_id);
    if (!branch) continue;

    // Match sales for this branch
    const branchSales = sales.filter(s => s.branch_id === shift.branch_id);
    if (branchSales.length === 0) continue;

    // Extract the shift-specific revenue column
    const shiftRevenueKey =
      shift.shift === 'morning' ? 'morning_revenue' :
      shift.shift === 'afternoon' ? 'afternoon_revenue' :
      shift.shift === 'evening' ? 'evening_revenue' : null;

    if (!shiftRevenueKey) continue;

    // Filter by day_of_week if specified
    let relevantSales = branchSales;
    if (shift.day_of_week !== null && shift.day_of_week !== undefined) {
      relevantSales = branchSales.filter(s => {
        if (!s.sale_date) return false;
        return new Date(s.sale_date).getDay() === shift.day_of_week;
      });
    }

    if (relevantSales.length === 0) continue;

    // Compute averages (last 30 days of available data)
    const recent = relevantSales.slice(0, 30);
    const avgRevenue = recent.reduce(
      (sum, s) => sum + parseFloat(s[shiftRevenueKey] || 0),
      0
    ) / recent.length;

    const laborCost = parseFloat(shift.labor_cost_per_shift || 0);
    if (laborCost === 0) continue;

    const ratio = laborCost / Math.max(avgRevenue, 1);
    const revenuePerLaborSar = avgRevenue / laborCost;

    // Thresholds: healthy industry labor cost = 20-30% of revenue
    // > 35% = watch, > 60% = unprofitable
    let status = 'healthy';
    if (ratio > 0.60) status = 'unprofitable';
    else if (ratio > 0.35) status = 'watch';

    // Estimated monthly waste = only the EXCESS over healthy threshold (30%)
    // Assumes shift runs daily (~30 times/month)
    const healthyLaborCost = avgRevenue * 0.30;
    const excessPerShift = Math.max(0, laborCost - healthyLaborCost);
    const estimatedMonthlyWaste = excessPerShift * 30;

    results.push({
      shift_id: shift.id,
      branch_id: shift.branch_id,
      branch_name_ar: branch.name_ar,
      branch_name_en: branch.name_en,
      shift: shift.shift,
      staff_count: shift.staff_count,
      shift_hours: parseFloat(shift.shift_hours),
      avg_hourly_wage: parseFloat(shift.avg_hourly_wage),
      days_analyzed: recent.length,
      avg_daily_revenue: Math.round(avgRevenue * 100) / 100,
      avg_daily_labor_cost: laborCost,
      labor_to_revenue_ratio: Math.round(ratio * 1000) / 1000,
      revenue_per_labor_sar: Math.round(revenuePerLaborSar * 100) / 100,
      status,
      estimated_monthly_waste_sar: Math.round(estimatedMonthlyWaste)
    });
  }

  return results.sort((a, b) => b.labor_to_revenue_ratio - a.labor_to_revenue_ratio);
}

/**
 * Convert unprofitable shifts into financial_gap-style records
 * so they appear in the Financial Radar alongside other gaps.
 */
export function shiftsToGaps(shiftAnalyses, { lang = 'ar' } = {}) {
  return shiftAnalyses
    .filter(s => s.status === 'unprofitable' || (s.status === 'watch' && s.estimated_monthly_waste_sar > 1500))
    .map(s => {
      const shiftLabel_ar = {
        morning: 'الصباحية', afternoon: 'الظهر', evening: 'المسائية', late_night: 'الليلية'
      }[s.shift] || s.shift;
      const shiftLabel_en = {
        morning: 'Morning', afternoon: 'Afternoon', evening: 'Evening', late_night: 'Late Night'
      }[s.shift] || s.shift;

      return {
        id: `labor_gap_${s.shift_id}`,
        gap_type: 'labor_inefficiency',
        severity: s.status === 'unprofitable' ? 'critical' : 'high',
        title_ar: `وردية ${shiftLabel_ar} خاسرة — ${s.branch_name_ar}`,
        title_en: `Unprofitable ${shiftLabel_en} shift — ${s.branch_name_en}`,
        description_ar: `تكلفة العمالة ${s.avg_daily_labor_cost.toLocaleString()} ر.س/يوم مقابل إيراد ${Math.round(s.avg_daily_revenue).toLocaleString()} ر.س. نسبة العمالة للإيراد: ${(s.labor_to_revenue_ratio * 100).toFixed(0)}% (الصحي: أقل من 30%).`,
        description_en: `Labor cost SAR ${s.avg_daily_labor_cost.toLocaleString()}/day vs revenue SAR ${Math.round(s.avg_daily_revenue).toLocaleString()}. Labor-to-revenue: ${(s.labor_to_revenue_ratio * 100).toFixed(0)}% (healthy: under 30%).`,
        expected_value: Math.round(s.avg_daily_revenue * 0.30),
        actual_value: s.avg_daily_labor_cost,
        gap_amount: s.avg_daily_labor_cost - (s.avg_daily_revenue * 0.30),
        gap_percentage: s.labor_to_revenue_ratio * 100,
        estimated_monetary_loss: s.estimated_monthly_waste_sar,
        branch_id: s.branch_id,
        is_resolved: false,
        is_computed: true, // flag so UI knows this is live-computed
        detected_at: new Date().toISOString(),
        shift_detail: s
      };
    });
}

/**
 * Aggregate labor KPIs for dashboard display.
 */
export function aggregateLaborKpis(shiftAnalyses) {
  if (!shiftAnalyses.length) {
    return {
      total_monthly_labor_cost: 0,
      total_monthly_waste: 0,
      unprofitable_shifts: 0,
      healthy_shifts: 0,
      avg_labor_ratio: 0
    };
  }

  const total_monthly_labor_cost = shiftAnalyses.reduce(
    (s, a) => s + a.avg_daily_labor_cost * 30, 0
  );
  const total_monthly_waste = shiftAnalyses.reduce(
    (s, a) => s + a.estimated_monthly_waste_sar, 0
  );
  const unprofitable_shifts = shiftAnalyses.filter(a => a.status === 'unprofitable').length;
  const healthy_shifts = shiftAnalyses.filter(a => a.status === 'healthy').length;
  const avg_labor_ratio = shiftAnalyses.reduce(
    (s, a) => s + a.labor_to_revenue_ratio, 0
  ) / shiftAnalyses.length;

  return {
    total_monthly_labor_cost: Math.round(total_monthly_labor_cost),
    total_monthly_waste: Math.round(total_monthly_waste),
    unprofitable_shifts,
    healthy_shifts,
    avg_labor_ratio: Math.round(avg_labor_ratio * 1000) / 1000
  };
}
